import type { PublicClinicView, PublicProfileSettings } from '@leoclinic/types';
import type { BookingFormData, AppointmentFormData } from '@leoclinic/types';
import { getSupabaseClient } from '../supabase/client';
import { appointmentService } from '../appointments/appointment.service';

const DAY_LABELS: Record<string, string> = {
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sáb',
  sunday: 'Dom',
};

function formatBusinessHours(
  hours?: Record<string, { open: string; close: string } | null> | null,
): string {
  if (!hours) return 'Horário a definir';
  const parts: string[] = [];
  for (const [day, slot] of Object.entries(hours)) {
    if (slot?.open && slot?.close) {
      parts.push(`${DAY_LABELS[day] ?? day} ${slot.open}–${slot.close}`);
    }
  }
  return parts.length > 0 ? parts.join(' · ') : 'Horário a definir';
}

export class BookingService {
  /** Slug público → clinic_id */
  async resolveClinicId(slug: string): Promise<string | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('clinics')
      .select('id')
      .eq('slug', slug)
      .eq('status', 'active')
      .maybeSingle();
    if (error) throw error;
    return data?.id ?? null;
  }

  /** Dados públicos da clínica para o portal cliente */
  async getPublicClinic(slug: string): Promise<PublicClinicView | null> {
    const supabase = getSupabaseClient();
    const { data: clinic, error } = await supabase
      .from('clinics')
      .select('id, name, slug, email, phone, address, city, postal_code')
      .eq('slug', slug)
      .eq('status', 'active')
      .maybeSingle();

    if (error) throw error;
    if (!clinic) return null;

    const { data: settingsRows } = await supabase
      .from('settings')
      .select('key, value')
      .eq('clinic_id', clinic.id)
      .in('key', ['public_profile', 'business_hours', 'app_branding']);

    let profile: PublicProfileSettings = {};
    let businessHours: Record<string, { open: string; close: string } | null> | null = null;
    let logoUrl: string | null = null;

    for (const row of settingsRows ?? []) {
      if (row.key === 'public_profile') profile = (row.value as PublicProfileSettings) ?? {};
      if (row.key === 'business_hours') {
        businessHours = row.value as Record<string, { open: string; close: string } | null>;
      }
      if (row.key === 'app_branding') {
        const branding = row.value as { logo_url?: string | null } | null;
        logoUrl = branding?.logo_url ?? null;
      }
    }

    return {
      ...clinic,
      profile,
      hoursLabel: formatBusinessHours(businessHours),
      logoUrl,
    };
  }

  async listPublicTreatments(clinicId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('treatments')
      .select('id, name, price, duration_minutes, description, color')
      .eq('clinic_id', clinicId)
      .eq('status', 'active')
      .order('name');
    if (error) throw error;
    return data ?? [];
  }

  async getAppointmentSettings(clinicId: string) {
    const supabase = getSupabaseClient();
    const { data } = await supabase
      .from('settings')
      .select('value')
      .eq('clinic_id', clinicId)
      .eq('key', 'appointment_settings')
      .maybeSingle();
    const value = data?.value as { min_advance_hours?: number; max_advance_days?: number } | null;
    return {
      min_advance_hours: value?.min_advance_hours ?? 2,
      max_advance_days: value?.max_advance_days ?? 60,
    };
  }

  async getAvailableSlots(
    clinicId: string,
    therapistId: string,
    treatmentId: string,
    fromDate: string,
    toDate: string,
  ) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('get_available_slots', {
      p_clinic_id: clinicId,
      p_therapist_id: therapistId,
      p_treatment_id: treatmentId,
      p_from: fromDate,
      p_to: toDate,
    });
    if (error) throw error;
    return (data ?? []) as { start_at: string }[];
  }

  async listPublicTherapists(clinicId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('get_public_therapists', {
      p_clinic_id: clinicId,
    });
    if (error) {
      const { data: therapists, error: tErr } = await supabase
        .from('therapists')
        .select('id, specialty, color, employee_id')
        .eq('clinic_id', clinicId)
        .eq('status', 'active');
      if (tErr) throw tErr;
      const employeeIds = [...new Set((therapists ?? []).map((t) => t.employee_id))];
      const { data: employees } = await supabase
        .from('employees')
        .select('id, profile_id')
        .in('id', employeeIds);
      const profileIds = [...new Set((employees ?? []).map((e) => e.profile_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', profileIds);
      const empMap = new Map((employees ?? []).map((e) => [e.id, e.profile_id]));
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p.full_name]));
      return (therapists ?? []).map((t) => ({
        id: t.id,
        full_name: profileMap.get(empMap.get(t.employee_id) ?? '') ?? 'Terapeuta',
        specialty: t.specialty,
        color: t.color,
      }));
    }
    return data ?? [];
  }

  async createOnlineBooking(clinicId: string, form: BookingFormData) {
    const supabase = getSupabaseClient();
    const start = new Date(form.start_at);

    const { data: appointmentId, error: rpcError } = await supabase.rpc('create_online_booking', {
      p_clinic_id: clinicId,
      p_client_name: form.client_name,
      p_client_email: form.client_email,
      p_client_phone: form.client_phone,
      p_treatment_id: form.treatment_id,
      p_therapist_id: form.therapist_id,
      p_start_at: start.toISOString(),
      p_notes: form.notes ?? 'Marcação online',
    });

    if (!rpcError && appointmentId) {
      return { id: appointmentId as string };
    }

    if (rpcError && !rpcError.message.includes('Could not find the function')) {
      throw rpcError;
    }

    let clientId: string;
    const { data: existing } = await supabase
      .from('clients')
      .select('id')
      .eq('clinic_id', clinicId)
      .eq('email', form.client_email)
      .maybeSingle();

    if (existing) {
      clientId = existing.id;
    } else {
      const { data: newClient, error: clientError } = await supabase
        .from('clients')
        .insert({
          clinic_id: clinicId,
          full_name: form.client_name,
          email: form.client_email,
          phone: form.client_phone,
        })
        .select('id')
        .single();
      if (clientError) throw clientError;
      clientId = newClient.id;
    }

    const { data: treatment } = await supabase
      .from('treatments')
      .select('duration_minutes')
      .eq('id', form.treatment_id)
      .single();

    const duration = treatment?.duration_minutes ?? 60;
    const end = new Date(start.getTime() + duration * 60000);

    const aptData: AppointmentFormData = {
      client_id: clientId,
      therapist_id: form.therapist_id,
      treatment_id: form.treatment_id,
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      status: 'pending',
      notes: form.notes ?? 'Marcação online',
    };

    const created = await appointmentService.create(clinicId, aptData);
    return { id: created.id };
  }
}

export const bookingService = new BookingService();
